import { useCallback, useMemo } from 'react';
import { useAccessStations } from '@/app/flightAccess';
import { FlightAccessNote } from '@/components/FlightAccessNote';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DateTimeInput } from '@/components/ui/DateTimeInput';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { SortArrow } from '@/components/ui/SortArrow';
import { useTranslation } from '@/i18n';
import type { Key } from '@/i18n';
import { Named } from '@/components/ui/Named';
import { useAirportCities, useStationOptions } from '@/lib/api/airports';
import { dateOfInput, dayMonth, dayOffset, formatDrift, minutesBetween } from '@/lib/clock';
import { cx } from '@/lib/cx';
import { cityOf } from '@/types/airport';
import { defaultStation } from '@/types/flightAccess';
import type { InternationalArrival, InternationalArrivalSortField } from '@/types/internationalArrival';
import { ROWS_OPTIONS, useInternationalArrivalParams } from './useInternationalArrivalParams';
import { useInternationalArrivals } from './useInternationalArrivals';
import styles from './InternationalArrivalsPage.module.css';

/**
 * The international arrivals journal.
 *
 * **ADMIN and AAK only** — narrower than the board, and refused on the read too, so
 * the route is guarded rather than the screen merely hiding its controls.
 *
 * What the screen is FOR is the two passport-control times. Every other column on a
 * row has a feed behind it and will be re-stated by the next message that carries
 * it; `firstPax` and `lastPax` are reported by nothing at all, so they exist only if
 * somebody types them here. That is why the table leads with the movement a reader
 * identifies the flight by and ends with the columns they are here to fill.
 *
 * The STA range lives in the `sta` column's own filter cell, both ends optional.
 * It ranges on `sta` and not on `ata` or `br`, which take ranges too but are empty
 * until a flight lands — see the filter row below.
 */
export const InternationalArrivalsPage = () => {
  // The reader's own airports when their access names any — the same rule as the board.
  const stationsOnly = useAccessStations();
  const { params, criteria, update, reset, hasFilters } = useInternationalArrivalParams(stationsOnly);
  const { rows, currPage, total, isLastPage, isLoading, errorKey } = useInternationalArrivals(criteria);
  const stationOptions = useStationOptions(stationsOnly);
  const { t, locale } = useTranslation();

  const setSort = useCallback(
    (field: InternationalArrivalSortField) => {
      const sameField = params.sort === field;
      update({
        sort: field,
        dir: sameField && params.dir === 'descending' ? 'ascending' : 'descending',
      });
    },
    [params.sort, params.dir, update],
  );

  /**
   * The cities behind the origin codes on this page.
   *
   * Only the codes actually shown, and `orgn` is abroad by definition — that is what
   * makes an arrival international — so each costs one lookup, once per session. The
   * receiving airport has no column of its own: the picker above scopes the journal
   * to one station, so printing it on every row would repeat the filter fifty times.
   *
   * `orgn` does not always hold a code. A source that could not resolve one leaves
   * the station NAME, and a name matches no airport, so those rows fall through to
   * showing the value as stored — which is the right answer for them.
   */
  const shownCodes = useMemo(() => rows.map((row) => row.orgn), [rows]);
  const airportOf = useAirportCities(shownCodes);

  const cityFor = useCallback(
    (code: string | null): string => {
      const airport = airportOf(code);
      return airport ? cityOf(airport, locale) : '';
    },
    [airportOf, locale],
  );


  return (
    <div className={styles.page}>
      <div className={styles.main}>
        <div className={styles.toolbar}>
          <h1 className={styles.title}>{t('internationalArrivals.title')}</h1>
          {/* For a restricted user: the journal holds only their arrivals. */}
          <FlightAccessNote />
          <div className={styles.spacer} />
          <Button size="sm" onClick={reset} disabled={!hasFilters}>
            {t('internationalArrivals.resetFilters')}
          </Button>
        </div>

        {/* Which journal, as opposed to which of its rows. */}
        <div className={styles.boardFilters}>
          <label className={styles.boardFilter}>
            <span className={styles.boardFilterLabel}>{t('internationalArrivals.columns.airportIataCode')}</span>
            {/* Narrowed like the board's: the reader's own airports, "all" meaning all
                of theirs, and no "all" for a single airport. */}
            <Select
              options={stationOptions}
              {...(stationsOnly === null
                ? { placeholder: t('internationalArrivals.allStations') }
                : stationsOnly.length > 1
                  ? { placeholder: t('flightAccess.allYourStations') }
                  : {})}
              value={params.airport}
              active={params.airport !== defaultStation(stationsOnly)}
              onChange={(event) => update({ airport: event.target.value })}
              aria-label={t('internationalArrivals.columns.airportIataCode')}
            />
          </label>

          {/* <label className={styles.boardFilter}>
            <span className={styles.boardFilterLabel}>{t('internationalArrivals.state.label')}</span>
            <Select
              options={INTERNATIONAL_ARRIVAL_STATES.map((state) => ({
                value: state,
                label: t(`internationalArrivals.state.${state}`),
              }))}
              value={params.state}
              active={params.state !== 'active'}
              onChange={(event) =>
                update({ state: event.target.value as InternationalArrivalParams['state'] })
              }
              aria-label={t('internationalArrivals.state.label')}
            />
          </label> */}

        </div>

        {errorKey && (
          <p className={styles.error} role="alert">
            {t(errorKey)}
          </p>
        )}

        <div className={styles.tableWrap}>
          {isLoading && <div className={styles.loadingBar} aria-hidden="true" />}

          <table className={cx(styles.table, isLoading && styles.loading)}>
            <thead>
              <tr className={styles.headRow}>
                {COLUMNS.map((column) => (
                  <SortHead
                    key={column.field}
                    field={column.field}
                    labelKey={column.labelKey}
                    sort={params.sort}
                    dir={params.dir}
                    onSort={setSort}
                  />
                ))}
              </tr>

              {/*
                Filters under the columns they filter. The airport has its own
                picker above instead — a code is easier to choose from a list of the
                operation's own stations than to spell — and `spot` and `acType`,
                which the endpoint does accept, have none: nobody looks up an
                arrival by its stand. Both of those still work from the URL, so a
                saved link that narrows by either keeps going.

                The range sits under `sta` and nowhere else. `ata` and `br` take
                ranges too, but they are empty until a flight lands and a range on
                an empty column drops the row — so a box under either would quietly
                mean "and it has already happened", which is not what a date filter
                looks like it does.
              */}
              <tr className={styles.filterRow}>
                <th>
                  <input
                    className={styles.filterInput}
                    value={params.flt}
                    onChange={(event) => update({ flt: event.target.value.toUpperCase() })}
                    aria-label={t('internationalArrivals.columns.flt')}
                  />
                </th>
                <th>
                  <input
                    className={styles.filterInput}
                    value={params.orgn}
                    onChange={(event) => update({ orgn: event.target.value.toUpperCase() })}
                    aria-label={t('internationalArrivals.columns.orgn')}
                  />
                </th>

                {/* Both ends inclusive and each optional, so one alone is a
                    half-open range — from a date with no end, or everything up to
                    one. Each end is a date box and a time box, and the bounds are on
                    the dates: see components/ui/DateTimeInput for why the pair is
                    split, and why a time cannot carry the bound. */}
                <th>
                  <div className={styles.range}>
                    <DateTimeInput
                      value={params.from}
                      onChange={(value) => update({ from: value })}
                      defaultTime="00:00"
                      label={t('internationalArrivals.staFrom')}
                      maxDate={dateOfInput(params.to) || undefined}
                      inputClassName={cx(
                        styles.filterInput,
                        params.from !== '' && styles.filterInputActive,
                      )}
                    />
                    <DateTimeInput
                      value={params.to}
                      onChange={(value) => update({ to: value })}
                      // A day asked for as the end of the range means all of it.
                      defaultTime="23:59"
                      label={t('internationalArrivals.staTo')}
                      minDate={dateOfInput(params.from) || undefined}
                      inputClassName={cx(
                        styles.filterInput,
                        params.to !== '' && styles.filterInputActive,
                      )}
                    />
                  </div>
                </th>

                {COLUMNS.slice(3).map((column) => (
                  <th key={column.field} />
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.length === 0 && !isLoading ? (
                <tr>
                  <td colSpan={COLUMNS.length} className={styles.message}>
                    {t('internationalArrivals.nothingFound')}
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <InternationalArrivalRow
                    key={row.id}
                    row={row}
                    orgnCity={cityFor(row.orgn)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          // The page the server RETURNED: asking past the end answers with the last
          // one, so showing the requested number would put page 9's rows under a
          // control that says 12.
          page={currPage}
          rows={params.rows}
          total={total}
          countLabel={t('internationalArrivals.rowsFound', { count: total })}
          isLastPage={isLastPage}
          rowsOptions={ROWS_OPTIONS}
          onPage={(page) => update({ page })}
          onRows={(rowCount) => update({ rows: rowCount })}
          isLoading={isLoading}
        />
      </div>

    </div>
  );
};

/**
 * The columns, in order.
 *
 * A list rather than repeated markup: the head row, the filter row and the
 * "nothing found" colspan all have to agree on the count, and a list is the only
 * version of that which cannot drift.
 *
 * Every one of these is orderable through `sortBy` — the type guarantees it, and an
 * unknown name would be a 500 rather than a 400.
 */
const COLUMNS: ReadonlyArray<{ field: InternationalArrivalSortField; labelKey: Key }> = [
  { field: "flt", labelKey: "internationalArrivals.columns.flt" },
  { field: "orgn", labelKey: "internationalArrivals.columns.orgn" },
  { field: "sta", labelKey: "internationalArrivals.columns.sta" },
  { field: "ata", labelKey: "internationalArrivals.columns.ata" },
  { field: "br", labelKey: "internationalArrivals.columns.br" },
  { field: "firstPax", labelKey: "internationalArrivals.columns.firstPax" },
  { field: "lastPax", labelKey: "internationalArrivals.columns.lastPax" },
  { field: "firstBag", labelKey: "internationalArrivals.columns.firstBag" },
  { field: "lastBag", labelKey: "internationalArrivals.columns.lastBag" },
  { field: "ttlPax", labelKey: "internationalArrivals.columns.ttlPax" },
  { field: "ttlBag", labelKey: "internationalArrivals.columns.ttlBag" },
  { field: "spot", labelKey: "internationalArrivals.columns.spot" },
  { field: "acType", labelKey: "internationalArrivals.columns.acType" },
];

/**
 * A sortable column head.
 *
 * At module level, not inside the page: a component created during a render is a new
 * type every time, so React unmounts and remounts its whole subtree — which in a
 * table means the filter input below it loses focus mid-word.
 */
const SortHead = ({
  field,
  labelKey,
  sort,
  dir,
  onSort,
}: {
  field: InternationalArrivalSortField;
  labelKey: Key;
  sort: InternationalArrivalSortField;
  dir: 'ascending' | 'descending';
  onSort: (field: InternationalArrivalSortField) => void;
}) => {
  const { t } = useTranslation();
  const sorted = sort === field;

  return (
    <th aria-sort={sorted ? dir : 'none'}>
      <button
        type="button"
        className={cx(styles.sortButton, sorted && styles.sorted)}
        onClick={() => onSort(field)}
        title={t('internationalArrivals.sortHint')}
      >
        {/*
          What the column means, then the API's own field name under it.

          The label leads because that is what a column header is for, and it makes
          this table read like every other one in the app. The name stays because
          the journal is read beside the API it comes from — a value is explained by
          following `airportScheduleId` into the schedule and the monitor, both of
          which speak in these names — so the column also says `firstBag`. The name
          is the one thing identical in all three languages, which is why it is not
          translated; the line above it is.
        */}
        <span className={styles.headStack}>
          <span className={styles.headLabel}>{t(labelKey)}</span>
          <span className={styles.headField}>{field}</span>
        </span>

        <SortArrow
          sorted={sorted}
          dir={dir}
          className={styles.sortArrow}
          idleClassName={styles.sortIdle}
        />
      </button>
    </th>
  );
};

/** One arrival. */
const InternationalArrivalRow = ({
  row,
  orgnCity,
}: {
  row: InternationalArrival;
  /** The origin's city. Empty for a source that left a station name, not a code. */
  orgnCity: string;
}) => {
  const { t } = useTranslation();

  return (
    <tr className={cx(!row.active && styles.withdrawnRow)}>

      {/* Withdrawn rows are returned by this endpoint whenever `state=all` is
          asked for, so the row says so rather than only looking faded. */}
      <td className={styles.flt}>
        <span className={styles.fltInner}>
          {row.flt ?? <Empty />}
          {!row.active && <Badge tone="neutral">{t("internationalArrivals.state.withdrawn")}</Badge>}
        </span>
      </td>

      <td>
        <Named code={row.orgn} name={orgnCity} showCode={true} />
      </td>

      {/*
        The three real timestamps, dated.

        Every one of them carries its date, unlike the board's — which drops it when
        the period is today, because there the reader chose the day. Here they did
        not: the range defaults to the last day but is editable to any span, so a
        page can cover months and a bare `02:34` would be a time on no particular day.

        `ata` is read against `sta` and `br` against `ata`, which is what each is
        FOR: the first says whether the flight was early or late, the second is the
        taxi. Only the first is coloured — a taxi is not late, it is just long.
      */}
      <td className={styles.time}>
        <Clock value={row.sta} />
      </td>
      <td className={styles.time}>
        <Clock value={row.ata} reference={row.sta} withDelta />
      </td>
      <td className={styles.time}>
        <Clock value={row.br} reference={row.ata} />
      </td>

      {/* The four handling fields. `varchar`, so printed as stored — see `Raw`. */}
      <td className={styles.handling}>
        <Raw value={row.firstPax} />
      </td>
      <td className={styles.handling}>
        <Raw value={row.lastPax} />
      </td>
      <td className={styles.handling}>
        <Raw value={row.firstBag} />
      </td>
      <td className={styles.handling}>
        <Raw value={row.lastBag} />
      </td>

      <td className={styles.numeric}>
        <Raw value={row.ttlPax} />
      </td>
      <td className={styles.numeric}>
        <Raw value={row.ttlBag} />
      </td>

      <td className={styles.mono}>{row.spot ?? <Empty />}</td>
      <td className={styles.mono}>{row.acType ?? <Empty />}</td>

    </tr>
  );
};

/**
 * A stored timestamp, with its date and — where one is asked for — how far it sits
 * from another.
 *
 * The same idiom as the board's clock, and it shares its helpers from `lib/clock`.
 * Only `sta`, `ata` and `br` come through here: they are real timestamps and arrive
 * as ISO with an offset. The `varchar` fields go through `Raw` instead, because a
 * date formatter answers "Invalid Date" for a perfectly good hand-typed note.
 */
const Clock = ({
  value,
  reference,
  withDelta = false,
}: {
  value: string | null;
  reference?: string | null;
  withDelta?: boolean;
}) => {
  const { formatTime, formatDateTime } = useTranslation();

  if (value === null) return <Empty />;

  const offset = reference ? dayOffset(reference, value) : 0;
  const drift = reference ? minutesBetween(reference, value) : 0;

  return (
    <span title={formatDateTime(value)}>
      <span className={styles.cellDate}>{dayMonth(value)}</span>
      {formatTime(value)}

      {/* A movement that landed on another date than the one it is measured
          against, written the way a timetable writes it. */}
      {offset !== 0 && (
        <sup className={styles.dayOffset}>{offset > 0 ? `+${offset}` : offset}</sup>
      )}

      {withDelta && drift !== 0 && (
        <span className={cx(styles.drift, drift > 0 ? styles.driftLate : styles.driftEarly)}>
          {formatDrift(drift)}
        </span>
      )}
    </span>
  );
};

/**
 * One of the six `varchar(100)` fields, printed as stored.
 *
 * Not parsed, not formatted, not right-aligned as a number would be if it were one.
 * `ttlPax` usually holds `"29"` and `firstBag` usually holds a timestamp, but the
 * column accepts free text and the two passport fields are typed by hand — so the
 * only honest thing to render is the string.
 */
const Raw = ({ value }: { value: string | null }) => {
  const text = (value ?? '').trim();
  return text === '' ? <Empty /> : <>{text}</>;
};


const Empty = () => <span className={styles.empty}>—</span>;
